import { IonButton, useIonRouter } from '@ionic/react';
import clsx from 'clsx';
import React, { useCallback, useEffect, useLayoutEffect, useReducer, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useHistory, useLocation } from 'react-router';
import useLocalStorage from 'common/reactHooks/useLocalStorage';
import { useAppSelector } from 'appRedux/createStore';
import {
  computeDrawerLowerShiftPx,
  computeDrawerOpenHeight,
  measureDrawerContentPx,
} from 'lib/drawerFit';
import { useExploreReady } from 'lib/exploreReadySignal';
import {
  DrawerPhase,
  LOGIN_FADE_MS,
  cardRevealTotalMs,
  clearLoginCasinoReveal,
  closeToWaitingMs,
  decideLoginReveal,
  drawerReducer,
  getDrawerTiming,
  hasShownRandomOutcomesThisSession,
  isDrawerLocked,
  markRandomOutcomesShownThisSession,
  peekLoginCasinoReveal,
  rubberBandOffset,
  setRandomOutcomesActive,
} from 'lib/loginCasinoReveal';
import { isExplorePath } from 'lib/linkPlayerFlow';

/**
 * Login → casino transition.
 *
 * Mounted by Home. ChooseCasinos opens a casino (the last-visited one on login, or the
 * player's pick) and, on the FIRST casino entry of the login session, leaves a session
 * flag; this host reads it (on mount and whenever Explore is entered again) and either
 *   - 'drawer': presents the Random Outcomes disclaimer as a locked bottom drawer
 *     (open height fits its content, max 90%; ease-out ~0.7s) over the dimmed property
 *     screen. Accept lowers it to Explore's resting position (snappy ease-out, 0.5s) and,
 *     HANDOFF_LEAD_MS before that slide ends, cross-fades it out while the Insight/Timeframe
 *     sections slide in and the EGM cards ease in one by one (50ms apart) underneath, so
 *     the phases overlap instead of running back to back. Decline signs out as before.
 *   - 'fade': a stale flag after Random Outcomes was already accepted this session →
 *     a light fade-in, no drawer.
 *
 * Random Outcomes shows once per logged-in session (every casino, every
 * LinkPlayerflowVersion). Accept marks the session (sessionStorage, keyed to the login,
 * cleared on sign-in / logout); localStorage 'legalDisclaimer' is still written as a
 * record but no longer decides anything.
 *
 * The map / casino data is fetched by MapStats as soon as Home mounts (it is
 * already behind the drawer), so nothing here touches any data path.
 */

const AGREED_STORAGE_KEY = 'legalDisclaimer';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const HTML_CARDS_HOLD = 'ro-cards-hold';
const HTML_CARDS_REVEAL = 'ro-cards-reveal';
const HTML_LOGIN_FADE = 'login-fade-in';

function prefersReducedMotion(): boolean {
  try {
    return (
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia(REDUCED_MOTION_QUERY).matches
    );
  } catch {
    return false;
  }
}

/** Light fade for users who already agreed (no drawer). */
const LoginFadeIn: React.FC<{ onDone?: () => void }> = ({ onDone }) => {
  useEffect(() => {
    clearLoginCasinoReveal();
    const root = document.documentElement;
    root.classList.add(HTML_LOGIN_FADE);
    const t = window.setTimeout(() => {
      root.classList.remove(HTML_LOGIN_FADE);
      onDone?.();
    }, LOGIN_FADE_MS + 100);
    return () => {
      window.clearTimeout(t);
      root.classList.remove(HTML_LOGIN_FADE);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
};

const LockIcon: React.FC = () => (
  <svg
    className="ro-drawer__lock"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <rect x="5" y="11" width="14" height="10" rx="2" fill="currentColor" />
    <path
      d="M8 11V8a4 4 0 0 1 8 0v3"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const RandomOutcomesDrawer: React.FC<{ onDone?: () => void }> = ({ onDone }) => {
  const history = useHistory();
  const router = useIonRouter();
  // Kept as a record of consent; the once-per-session marker decides when it shows.
  const [, setAgreed] = useLocalStorage<boolean>(AGREED_STORAGE_KEY, false);
  const loginKey = useAppSelector((state) => state.app.persistedState.userInfo?.appUserPk);
  const exploreReady = useExploreReady();
  const [reducedMotion] = useState(prefersReducedMotion);
  const timing = getDrawerTiming(reducedMotion);

  const [phase, dispatch] = useReducer(drawerReducer, 'entering' as DrawerPhase);
  const [shown, setShown] = useState(false);
  const [band, setBand] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [hint, setHint] = useState(false);
  const dragStartY = useRef<number | null>(null);
  const hintTimer = useRef<number | null>(null);
  const unblockRef = useRef<(() => void) | null>(null);

  const locked = isDrawerLocked(phase);

  // Open height = measured content height (see lib/drawerFit): re-measured on resize,
  // orientation / visual-viewport changes, text-size / zoom changes (ResizeObserver on the
  // rendered rows) and when web fonts finish loading. Capped at 90% of the screen; past
  // the cap the text area scrolls and the buttons stay pinned.
  const rootRef = useRef<HTMLDivElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState<{ height: number; shift: number } | null>(null);
  const gone = phase === 'done';

  useLayoutEffect(() => {
    const root = rootRef.current;
    const drawer = drawerRef.current;
    if (gone || !root || !drawer) return undefined;

    let raf = 0;
    let disposed = false;
    const measure = () => {
      raf = 0;
      if (disposed) return;
      const viewport = root.getBoundingClientRect().height || window.innerHeight;
      const height = computeDrawerOpenHeight({
        contentPx: measureDrawerContentPx(drawer),
        viewportPx: viewport,
      });
      if (height == null) return; // not laid out yet: keep the CSS fallback
      const shift = computeDrawerLowerShiftPx(height, viewport);
      setFit((prev) =>
        prev && prev.height === height && prev.shift === shift ? prev : { height, shift }
      );
    };
    const schedule = () => {
      if (!raf) raf = window.requestAnimationFrame(measure);
    };

    measure(); // before first paint, so the slide-up already uses the fitted height

    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null;
    if (observer) {
      observer.observe(root);
      drawer.querySelectorAll('.ro-drawer__inner > *, .ro-drawer__scroll > *').forEach((el) => {
        observer.observe(el);
      });
    }
    window.addEventListener('resize', schedule);
    window.addEventListener('orientationchange', schedule);
    const visualViewport = window.visualViewport;
    visualViewport?.addEventListener('resize', schedule);
    document.fonts?.ready.then(schedule).catch(() => undefined);

    return () => {
      disposed = true;
      if (raf) window.cancelAnimationFrame(raf);
      observer?.disconnect();
      window.removeEventListener('resize', schedule);
      window.removeEventListener('orientationchange', schedule);
      visualViewport?.removeEventListener('resize', schedule);
    };
  }, [gone]);

  // Slide up on the next frames so the hidden state paints first.
  useEffect(() => {
    document.documentElement.classList.add(HTML_CARDS_HOLD);
    let raf2 = 0;
    const raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(() => setShown(true));
    });
    return () => {
      window.cancelAnimationFrame(raf1);
      window.cancelAnimationFrame(raf2);
      document.documentElement.classList.remove(HTML_CARDS_HOLD);
      document.documentElement.classList.remove(HTML_CARDS_REVEAL);
      if (hintTimer.current) window.clearTimeout(hintTimer.current);
    };
  }, []);

  // On screen until 'done': the Link Player drawer (Version 2) waits for this, so the
  // two drawers never show together.
  const finished = phase === 'done';
  useEffect(() => {
    setRandomOutcomesActive(!finished);
    if (finished) onDone?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finished]);
  useEffect(() => () => setRandomOutcomesActive(false), []);

  useEffect(() => {
    if (!shown || phase !== 'entering') return undefined;
    const t = window.setTimeout(() => dispatch({ type: 'entered' }), timing.open);
    return () => window.clearTimeout(t);
  }, [shown, phase, timing.open]);

  useEffect(() => {
    if (phase === 'open') {
      document.getElementById('agreeButton')?.focus();
    }
  }, [phase]);

  // Locked: the browser/OS back gesture must not leave (or dismiss) the drawer.
  useEffect(() => {
    if (!locked) return undefined;
    const unblock = history.block(() => false);
    unblockRef.current = unblock;
    return () => {
      unblock();
      if (unblockRef.current === unblock) unblockRef.current = null;
    };
  }, [locked, history]);

  // Android hardware back (Capacitor/Ionic): swallow while locked.
  useEffect(() => {
    if (!locked) return undefined;
    const onBack = (ev: Event) => {
      const detail = (ev as CustomEvent<{ register?: (priority: number, cb: () => void) => void }>)
        .detail;
      detail?.register?.(100000, () => {
        /* locked: Accept or Decline only */
      });
    };
    document.addEventListener('ionBackButton', onBack);
    return () => document.removeEventListener('ionBackButton', onBack);
  }, [locked]);

  // The slide-down keeps running (same `ro-drawer--rest` class in closing / waiting /
  // handoff); the phase just advances HANDOFF_LEAD_MS before it ends so the hand-off
  // overlaps the slide's tail instead of waiting for it.
  useEffect(() => {
    if (phase !== 'closing') return undefined;
    const t = window.setTimeout(() => dispatch({ type: 'closed' }), closeToWaitingMs(timing));
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, timing.close, timing.handoffLead]);

  // Hand off as soon as Explore's own drawer is mounted (it already is underneath in the
  // normal case, so there is no pause). If it is not up yet a spinner shows meanwhile; the
  // max wait keeps a failed load from trapping the user.
  useEffect(() => {
    if (phase !== 'waiting') return undefined;
    const delay = exploreReady ? timing.handoffSettle : timing.waitMax;
    const t = window.setTimeout(() => dispatch({ type: 'handoff' }), delay);
    return () => window.clearTimeout(t);
  }, [phase, exploreReady, timing.handoffSettle, timing.waitMax]);

  useEffect(() => {
    if (phase !== 'handoff') return undefined;
    const t = window.setTimeout(() => dispatch({ type: 'done' }), timing.handoffFade);
    return () => window.clearTimeout(t);
  }, [phase, timing.handoffFade]);

  // Hand-off starts → ease the EGM cards in one by one (and, via the hold class swap, slide
  // the Insight/Timeframe sections back in) while the drawer is still fading out above
  // them, then clean up the classes.
  const revealing = phase === 'handoff' || phase === 'done';
  useEffect(() => {
    if (!revealing) return undefined;
    const root = document.documentElement;
    root.classList.remove(HTML_CARDS_HOLD);
    root.classList.add(HTML_CARDS_REVEAL);
    const t = window.setTimeout(
      () => root.classList.remove(HTML_CARDS_REVEAL),
      cardRevealTotalMs(timing)
    );
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealing]);

  const handleAgree = () => {
    if (!locked) return;
    setAgreed(true);
    // Once per login session: later casino entries in this session skip it.
    markRandomOutcomesShownThisSession(loginKey);
    clearLoginCasinoReveal();
    dispatch({ type: 'accept' });
  };

  // Same as the modal's No path: sign out (Logout clears state and returns to login).
  const handleDisagree = () => {
    if (!locked) return;
    unblockRef.current?.();
    clearLoginCasinoReveal();
    router.push('/logout');
  };

  // Locked drawer: a pull-down rubber-bands back and nudges "Accept to continue".
  const onHandlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!locked) return;
    dragStartY.current = e.clientY;
    setDragging(true);
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* pointer capture is best-effort */
    }
  };
  const onHandlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (dragStartY.current == null) return;
    setBand(rubberBandOffset(e.clientY - dragStartY.current));
  };
  const endHandleDrag = () => {
    if (dragStartY.current == null) return;
    const pulled = band > 0;
    dragStartY.current = null;
    setDragging(false);
    setBand(0);
    if (pulled) {
      setHint(true);
      if (hintTimer.current) window.clearTimeout(hintTimer.current);
      hintTimer.current = window.setTimeout(() => setHint(false), 1800);
    }
  };

  if (phase === 'done') return null;

  const atRest = phase === 'closing' || phase === 'waiting' || phase === 'handoff';
  const backdropClass =
    phase === 'entering' && !shown
      ? 'ro-drawer__backdrop--hidden'
      : locked
        ? 'ro-drawer__backdrop--visible'
        : 'ro-drawer__backdrop--lifting';

  return ReactDOM.createPortal(
    <div className="ro-drawer-root" data-phase={phase} ref={rootRef}>
      {/* No onClick: tapping the dimmed screen never dismisses the drawer. */}
      <div
        className={clsx(
          'ro-drawer__backdrop',
          backdropClass,
          phase === 'handoff' && 'ro-drawer__backdrop--inert'
        )}
        aria-hidden="true"
      />
      <div
        ref={drawerRef}
        style={
          fit
            ? ({
                '--ro-drawer-height': `${fit.height}px`,
                '--ro-drawer-lower-shift': `${fit.shift}px`,
              } as React.CSSProperties)
            : undefined
        }
        role="dialog"
        aria-modal="true"
        aria-labelledby="ro-drawer-title"
        aria-describedby="ro-drawer-text"
        className={clsx(
          'ro-drawer m-legal__form',
          !atRest && shown && 'ro-drawer--open',
          atRest && 'ro-drawer--rest',
          phase === 'waiting' && 'ro-drawer--waiting',
          phase === 'handoff' && 'ro-drawer--out'
        )}
      >
        <div
          className={clsx('ro-drawer__inner', !dragging && 'ro-drawer__inner--settle')}
          style={{ transform: band ? `translateY(${band}px)` : undefined }}
        >
          <div
            className="ro-drawer__handle"
            onPointerDown={onHandlePointerDown}
            onPointerMove={onHandlePointerMove}
            onPointerUp={endHandleDrag}
            onPointerCancel={endHandleDrag}
          >
            <span className="ro-drawer__handle-bar" />
            <span className="ro-drawer__locked-pill">
              <LockIcon />
              Locked until you accept
            </span>
          </div>

          <header className="ro-drawer__header">
            <div className="title" id="ro-drawer-title">
              Random Outcomes
            </div>
          </header>

          <div className="ro-drawer__scroll" tabIndex={0} id="ro-drawer-text">
            <p className="text">
              Slot machine outcomes are determined by a random number generator (RNG) and are
              independent and unpredictable. Historical data from Slot Check is for informational
              purposes only and reflects past results. Slot Check does not predict, influence, or
              determine future outcomes. By using Slot Check, you acknowledge that it is not
              responsible for any use of this data that assumes future results can be predicted, and
              you agree to this disclaimer.
            </p>
          </div>

          <div className="ro-drawer__hint" role="status" aria-live="polite">
            {hint ? 'Accept to continue' : ''}
          </div>

          <footer className="ro-drawer__actions">
            <IonButton
              onClick={handleAgree}
              id="agreeButton"
              expand="block"
              class="legal-yes-button"
              disabled={!locked}
            >
              Yes, I agree
            </IonButton>
            <IonButton
              fill="clear"
              onClick={handleDisagree}
              id="disagreeButton"
              expand="full"
              class="legal-no-button"
              disabled={!locked}
            >
              No, I do not agree
            </IonButton>
          </footer>
        </div>
      </div>

      {phase === 'waiting' && !exploreReady && (
        <div className="ro-drawer__spinner" role="status" aria-label="Loading casino">
          <span className="ro-drawer__spinner-ring" />
        </div>
      )}
    </div>,
    document.body
  );
};

export const LoginCasinoRevealHost: React.FC = () => {
  const location = useLocation();
  const loginKey = useAppSelector((state) => state.app.persistedState.userInfo?.appUserPk);
  const decide = () =>
    decideLoginReveal({
      flag: peekLoginCasinoReveal(),
      shownThisSession: hasShownRandomOutcomesThisSession(loginKey),
    });
  const [reveal, setReveal] = useState(() => ({ mode: decide(), key: 0 }));
  const onExplore = isExplorePath(location.pathname);

  // Home can stay mounted (Ionic keeps the tabs page) while the player goes to Select
  // Casino and picks again, so re-check every time Explore is entered, not just on mount.
  // Only on Explore: the drawer is portalled to <body> and must never cover Select Casino
  // or the Version 1 Link Player screen.
  useEffect(() => {
    if (!onExplore) return;
    setReveal((prev) => {
      if (prev.mode !== 'none') return prev; // one at a time
      const next = decide();
      return next === 'none' ? prev : { mode: next, key: prev.key + 1 };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onExplore, location.key]);

  const finish = useCallback(() => setReveal((prev) => ({ mode: 'none', key: prev.key })), []);

  if (reveal.mode === 'drawer') return <RandomOutcomesDrawer key={reveal.key} onDone={finish} />;
  if (reveal.mode === 'fade') return <LoginFadeIn key={reveal.key} onDone={finish} />;
  return null;
};

export default LoginCasinoRevealHost;
